import {
	Children,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useEffectOnce } from 'react-use';
import arrowLeft from '~/assets/icons/arrow_left_3.svg';
import arrowRight from '~/assets/icons/arrow_right.svg';
import { ImgIcon } from '~/components/ui/ImgIcon';
import { Text } from '~/components/ui/Text';
import { MenuItem } from '../MenuItem';
import { TUNING_STUDIO_CARD_WIDTH } from '../MenuItem/MenuItem.model';
import {
	StyledArrowButton,
	StyledContainer,
	StyledCountContainer,
	StyledCountValueContainer,
	StyledHorizontalList,
	StyledListWrapper,
	StyledNotActiveCategory,
	StyledTitleContainer,
} from './Categories.styled';

const ITEM_GAP = 10;
const PADDING_LEFT = 60;
const PADDING_RIGHT = 60;
const PLACEHOLDER_MIN_COUNT = 1;
const MASK_SIZE = 30;

const Categories = ({
	name,
	activeIndex,
	totalCount,
	children,
	handleBack,
	handlePrev,
	handleNext,
}: {
	name: string;
	activeIndex: number;
	totalCount: number;
	children: React.ReactNode;
	handleBack?: () => void;
	handlePrev?: () => void;
	handleNext?: () => void;
}) => {
	const listRef = useRef<HTMLDivElement | null>(null);
	const contentRef = useRef<HTMLDivElement | null>(null);
	const [placeholderCount, setPlaceholderCount] = useState(0);
	const [showLeftMask, setShowLeftMask] = useState(false);
	const disableWheelScroll = false;

	const childrenCount = Children.count(children);
	const hasPaddingLeft = !handleBack;
	const displayedIndex =
		totalCount > 0 ? Math.min(activeIndex + 1, totalCount) : 0;

	const calculatePlaceholders = useCallback(() => {
		const container = listRef.current;
		if (!container) return;

		const containerWidth =
			container.clientWidth - (hasPaddingLeft ? PADDING_LEFT : 0);
		const realContentWidth =
			childrenCount * (TUNING_STUDIO_CARD_WIDTH + ITEM_GAP) -
			ITEM_GAP +
			PADDING_RIGHT;
		const itemsWidth = childrenCount * (TUNING_STUDIO_CARD_WIDTH + ITEM_GAP);

		if (realContentWidth > containerWidth) {
			setPlaceholderCount(0);
			return;
		}

		const remainingWidth = containerWidth - itemsWidth;

		if (remainingWidth > 0) {
			const neededPlaceholders =
				Math.ceil(remainingWidth / (TUNING_STUDIO_CARD_WIDTH + ITEM_GAP)) + 1;
			setPlaceholderCount(Math.max(PLACEHOLDER_MIN_COUNT, neededPlaceholders));
		}
	}, [childrenCount, hasPaddingLeft]);

	useEffect(() => {
		calculatePlaceholders();
		window.addEventListener('resize', calculatePlaceholders);
		return () => window.removeEventListener('resize', calculatePlaceholders);
	}, [calculatePlaceholders]);

	useEffect(() => {
		const container = listRef.current;
		if (!container) return;

		const containerWidth =
			container.clientWidth - (hasPaddingLeft ? PADDING_LEFT : 0);
		const realContentWidth =
			childrenCount * (TUNING_STUDIO_CARD_WIDTH + ITEM_GAP) -
			ITEM_GAP +
			PADDING_RIGHT;

		if (realContentWidth <= containerWidth) {
			container.scrollLeft = 0;
			return;
		}

		const itemPosition = activeIndex * (TUNING_STUDIO_CARD_WIDTH + ITEM_GAP);
		const itemCenter = itemPosition + TUNING_STUDIO_CARD_WIDTH / 2;
		const containerCenter = containerWidth / 2;

		let targetScroll = itemCenter - containerCenter;

		const maxScroll = realContentWidth - containerWidth;
		targetScroll = Math.max(0, Math.min(targetScroll, maxScroll));

		container.scrollLeft = targetScroll;
	}, [activeIndex, childrenCount, hasPaddingLeft]);

	useEffect(() => {
		const element = listRef.current;
		if (!element) return;

		if (!handleBack) {
			setShowLeftMask(false);
			return;
		}

		const updateMaskVisibility = () => {
			const maxScrollLeft = element.scrollWidth - element.clientWidth;
			setShowLeftMask(maxScrollLeft > 0 && element.scrollLeft > 0);
		};

		element.addEventListener('scroll', updateMaskVisibility);
		window.addEventListener('resize', updateMaskVisibility);
		updateMaskVisibility();

		return () => {
			element.removeEventListener('scroll', updateMaskVisibility);
			window.removeEventListener('resize', updateMaskVisibility);
		};
	}, [childrenCount, handleBack]);

	useEffect(() => {
		const element = listRef.current;
		if (!element || disableWheelScroll) {
			return;
		}

		const mouseScroll = (e: WheelEvent) => {
			e.preventDefault();

			// Учитываем padding-left в доступной ширине
			const containerWidth =
				element.clientWidth - (hasPaddingLeft ? PADDING_LEFT : 0);
			// Ограничиваем скролл: последний реальный элемент + паддинг у правого края
			const realContentWidth =
				childrenCount * (TUNING_STUDIO_CARD_WIDTH + ITEM_GAP) -
				ITEM_GAP +
				PADDING_RIGHT;
			const maxScroll = Math.max(0, realContentWidth - containerWidth);

			const newScrollLeft = element.scrollLeft + e.deltaY;
			element.scrollLeft = Math.max(0, Math.min(newScrollLeft, maxScroll));
		};

		element.addEventListener('wheel', mouseScroll);

		return () => {
			element.removeEventListener('wheel', mouseScroll);
		};
	}, [disableWheelScroll, childrenCount, hasPaddingLeft]);

	useEffectOnce(() => {
		const preventArrowScroll = (e: KeyboardEvent) => {
			if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
				e.preventDefault();
			}
		};

		document.addEventListener('keydown', preventArrowScroll);
		return () => document.removeEventListener('keydown', preventArrowScroll);
	});

	const placeholders = useMemo(() => {
		return Array.from({ length: placeholderCount }, (_, i) => (
			<StyledNotActiveCategory key={`placeholder-${i}`} />
		));
	}, [placeholderCount]);

	return (
		<StyledContainer>
			<StyledListWrapper>
				{handleBack && (
					<MenuItem
						type="menu"
						name="Назад"
						icon={arrowLeft}
						count={0}
						onClick={handleBack}
						width={160}
						iconSize={45}
						iconWidth={27}
						iconOpacity={0.5}
						style={{ marginLeft: 60 }}
						noGlow
					/>
				)}
				<StyledHorizontalList
					ref={listRef}
					$isFull={!handleBack}
					style={{
						['--leftFade' as any]:
							handleBack && showLeftMask ? `${MASK_SIZE}px` : '0px',
					}}
				>
					<div
						ref={contentRef}
						style={{
							display: 'flex',
							gap: ITEM_GAP,
							paddingRight: PADDING_RIGHT,
						}}
					>
						{children}
						{placeholders}
					</div>
				</StyledHorizontalList>
			</StyledListWrapper>
			<StyledTitleContainer $isFull={!handleBack}>
				<Text>{name}</Text>
			</StyledTitleContainer>
			<StyledCountContainer>
				<StyledArrowButton onClick={handlePrev}>
					<ImgIcon icon={arrowLeft} width={10} height={10} />
				</StyledArrowButton>
				<StyledCountValueContainer>
					<Text>
						{displayedIndex} <span>/ {totalCount}</span>
					</Text>
				</StyledCountValueContainer>
				<StyledArrowButton onClick={handleNext}>
					<ImgIcon icon={arrowRight} width={10} height={10} />
				</StyledArrowButton>
			</StyledCountContainer>
		</StyledContainer>
	);
};

export { Categories };
